// Alliances (04 §5.14, visibility §3.8) and alliance events (§7.4). Diplomacy, wars, chat and
// broadcasts are not in the slice (first-playable-slice S-5).
import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';

export const ALLIANCE_RANKS = ['overlord', 'warlord', 'commander', 'recruit'] as const;
export const allianceRankSchema = z.enum(ALLIANCE_RANKS);
export type AllianceRank = z.infer<typeof allianceRankSchema>;

/** Higher number = higher rank; ranks inherit the permissions below them (GDD 05 §1.3). */
export const RANK_ORDER: Record<AllianceRank, number> = {
  recruit: 0,
  commander: 1,
  warlord: 2,
  overlord: 3,
};

/** GDD 05 §1.1 field limits. */
export const ALLIANCE_NAME_MIN_LENGTH = 3;
export const ALLIANCE_NAME_MAX_LENGTH = 30;
export const ALLIANCE_DESCRIPTION_MAX_LENGTH = 500;
export const ALLIANCE_MOTD_MAX_LENGTH = 500;
export const ALLIANCE_APPLICATION_MAX_LENGTH = 500;

export const allianceNameSchema = z
  .string()
  .trim()
  .min(ALLIANCE_NAME_MIN_LENGTH)
  .max(ALLIANCE_NAME_MAX_LENGTH)
  .refine((name) => !name.includes('  '), 'no double spaces');
/** 2–5 uppercase letters or digits, shown in brackets next to player names. */
export const allianceTagSchema = z
  .string()
  .regex(/^[A-Z0-9]{2,5}$/, '2–5 uppercase letters or digits');

/** G-65: the free emblem library — 12 icons × 8 colours. Premium emblems are not in the slice. */
export const ALLIANCE_EMBLEM_ICONS = [
  'star',
  'planet',
  'moon',
  'sun',
  'rocket',
  'shield',
  'swords',
  'crown',
  'skull',
  'eye',
  'atom',
  'flame',
] as const;
export type AllianceEmblemIcon = (typeof ALLIANCE_EMBLEM_ICONS)[number];
export const ALLIANCE_EMBLEM_COLORS = [
  '#67e8f9',
  '#34d399',
  '#fbbf24',
  '#f87171',
  '#c4b5fd',
  '#f472b6',
  '#60a5fa',
  '#cbd5e1',
] as const;
export const allianceEmblemSchema = z.strictObject({
  icon: z.enum(ALLIANCE_EMBLEM_ICONS),
  color: z.enum(ALLIANCE_EMBLEM_COLORS),
});
export type AllianceEmblem = z.infer<typeof allianceEmblemSchema>;

export const createAllianceRequestSchema = z.strictObject({
  name: allianceNameSchema,
  tag: allianceTagSchema,
  description: z.string().trim().max(ALLIANCE_DESCRIPTION_MAX_LENGTH).default(''),
  emblem: allianceEmblemSchema.nullable().default(null),
});
export type CreateAllianceRequest = z.input<typeof createAllianceRequestSchema>;

/** Overlord: profile, recruitment, name and tag (G-64: rename cooldown); Warlord+: MOTD. */
export const updateAllianceRequestSchema = z
  .strictObject({
    name: allianceNameSchema.optional(),
    tag: allianceTagSchema.optional(),
    description: z.string().trim().max(ALLIANCE_DESCRIPTION_MAX_LENGTH).optional(),
    emblem: allianceEmblemSchema.nullable().optional(),
    openRecruitment: z.boolean().optional(),
    motd: z.string().trim().max(ALLIANCE_MOTD_MAX_LENGTH).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'nothing to change');
export type UpdateAllianceRequest = z.infer<typeof updateAllianceRequestSchema>;

export const allianceMemberSchema = z.strictObject({
  playerId: idSchema,
  displayName: z.string(),
  rank: allianceRankSchema,
  joinedAt: isoDateTimeSchema,
  points: z.number().int().nonnegative(),
});
export type AllianceMember = z.infer<typeof allianceMemberSchema>;

const publicAllianceFields = {
  id: idSchema,
  name: z.string(),
  tag: z.string(),
  description: z.string(),
  emblem: allianceEmblemSchema.nullable(),
  openRecruitment: z.boolean(),
  memberCount: z.number().int().nonnegative(),
  maxMembers: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
};

/** Everyone in the galaxy (04 §3.8). */
export const publicAllianceSchema = z.strictObject({
  view: z.literal('public'),
  ...publicAllianceFields,
  /** Systems owned by the current members (P21). Not in the search list. */
  systemCount: z.number().int().nonnegative(),
  /** G-77: sum of building levels of owned systems (alliance: of its members). */
  points: z.number().int().nonnegative(),
  members: z.array(allianceMemberSchema),
});

/** Members additionally see the MOTD and their own rank. */
export const memberAllianceSchema = z.strictObject({
  view: z.literal('member'),
  ...publicAllianceFields,
  systemCount: z.number().int().nonnegative(),
  /** G-77: sum of building levels of owned systems (alliance: of its members). */
  points: z.number().int().nonnegative(),
  members: z.array(allianceMemberSchema),
  motd: z.string(),
  myRank: allianceRankSchema,
  /** G-64: the earliest next name/tag change; null before the first rename. */
  renameAllowedAt: isoDateTimeSchema.nullable(),
});

export const allianceSchema = z.discriminatedUnion('view', [
  publicAllianceSchema,
  memberAllianceSchema,
]);
export type Alliance = z.infer<typeof allianceSchema>;
export type MemberAlliance = z.infer<typeof memberAllianceSchema>;

export const allianceSummarySchema = z.strictObject(publicAllianceFields);
export type AllianceSummary = z.infer<typeof allianceSummarySchema>;

export const allianceSearchQuerySchema = z.object({
  search: z.string().trim().max(ALLIANCE_NAME_MAX_LENGTH).optional(),
});
export const allianceSearchResponseSchema = z.strictObject({
  items: z.array(allianceSummarySchema),
});
export type AllianceSearchResponse = z.infer<typeof allianceSearchResponseSchema>;

export const createAllianceResponseSchema = z.strictObject({
  alliance: memberAllianceSchema,
  rank: z.literal('overlord'),
});
export type CreateAllianceResponse = z.infer<typeof createAllianceResponseSchema>;

export const membershipSchema = z.strictObject({
  allianceId: idSchema,
  playerId: idSchema,
  rank: allianceRankSchema,
  joinedAt: isoDateTimeSchema,
  /** Recruits: when `alliance-recruit-promote` makes them Commander. */
  promoteAt: isoDateTimeSchema.nullable(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const membershipResponseSchema = z.strictObject({ membership: membershipSchema });
export type MembershipResponse = z.infer<typeof membershipResponseSchema>;

export const membershipsResponseSchema = z.strictObject({ memberships: z.array(membershipSchema) });
export type MembershipsResponse = z.infer<typeof membershipsResponseSchema>;

export const createInviteRequestSchema = z.strictObject({ playerId: idSchema });
export const createApplicationRequestSchema = z.strictObject({
  message: z.string().trim().max(ALLIANCE_APPLICATION_MAX_LENGTH).optional(),
});
export type CreateApplicationRequest = z.infer<typeof createApplicationRequestSchema>;

export const allianceInviteSchema = z.strictObject({
  id: idSchema,
  kind: z.enum(['invite', 'application']),
  alliance: z.strictObject({ id: idSchema, name: z.string(), tag: z.string() }),
  player: z.strictObject({ playerId: idSchema, displayName: z.string() }),
  invitedBy: z.strictObject({ playerId: idSchema, displayName: z.string() }).nullable(),
  message: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
});
export type AllianceInvite = z.infer<typeof allianceInviteSchema>;

export const allianceInviteResponseSchema = z.strictObject({ invite: allianceInviteSchema });
export type AllianceInviteResponse = z.infer<typeof allianceInviteResponseSchema>;

export const allianceInviteListResponseSchema = z.strictObject({
  items: z.array(allianceInviteSchema),
});
export type AllianceInviteListResponse = z.infer<typeof allianceInviteListResponseSchema>;

export const rankChangeRequestSchema = z.strictObject({
  rank: z.enum(['warlord', 'commander', 'recruit']),
});
export type RankChangeRequest = z.infer<typeof rankChangeRequestSchema>;

export const leadershipRequestSchema = z.strictObject({ playerId: idSchema });

// Players (04 §5.1).

export const playerSearchQuerySchema = z.object({
  search: z.string().trim().min(1).max(20),
});

export const playerSummarySchema = z.strictObject({
  playerId: idSchema,
  displayName: z.string(),
  allianceTag: z.string().nullable(),
});
export type PlayerSummary = z.infer<typeof playerSummarySchema>;

export const playerSearchResponseSchema = z.strictObject({ items: z.array(playerSummarySchema) });
export type PlayerSearchResponse = z.infer<typeof playerSearchResponseSchema>;

/** Public player (04 §3.8): never Credits, Artifacts, shield end time or cooldowns. */
export const publicPlayerSchema = z.strictObject({
  playerId: idSchema,
  displayName: z.string(),
  alliance: z.strictObject({ id: idSchema, tag: z.string(), rank: allianceRankSchema }).nullable(),
  shielded: z.boolean(),
  systems: z.array(z.strictObject({ id: idSchema, name: z.string(), generatedName: z.string() })),
  stats: z.strictObject({
    systemsOwned: z.number().int().nonnegative(),
    /** G-77: sum of the building levels of the owned systems. */
    points: z.number().int().nonnegative(),
  }),
});
export type PublicPlayer = z.infer<typeof publicPlayerSchema>;

// Rankings (G-77, G-78): points descending, equal points share a rank and are listed by name.

export const playerRankingSchema = z.strictObject({
  rank: z.number().int().positive(),
  playerId: idSchema,
  displayName: z.string(),
  alliance: z.strictObject({ id: idSchema, tag: z.string() }).nullable(),
  systems: z.number().int().nonnegative(),
  points: z.number().int().nonnegative(),
});
export type PlayerRanking = z.infer<typeof playerRankingSchema>;

export const allianceRankingSchema = z.strictObject({
  rank: z.number().int().positive(),
  id: idSchema,
  name: z.string(),
  tag: z.string(),
  emblem: allianceEmblemSchema.nullable(),
  members: z.number().int().nonnegative(),
  systems: z.number().int().nonnegative(),
  points: z.number().int().nonnegative(),
});
export type AllianceRanking = z.infer<typeof allianceRankingSchema>;

export const rankingsResponseSchema = z.strictObject({
  players: z.array(playerRankingSchema),
  alliances: z.array(allianceRankingSchema),
});
export type RankingsResponse = z.infer<typeof rankingsResponseSchema>;

// Events (04 §7.4).

export const allianceMembershipChangedSchema = z.strictObject({
  allianceId: idSchema,
  playerId: idSchema,
  rank: allianceRankSchema.nullable(),
});
export type AllianceMembershipChanged = z.infer<typeof allianceMembershipChangedSchema>;

export const allianceUpdatedSchema = allianceSummarySchema;
export const allianceDisbandedSchema = z.strictObject({ allianceId: idSchema });
export const allianceInviteReceivedSchema = z.strictObject({
  inviteId: idSchema,
  allianceId: idSchema,
});
export const allianceMotdChangedSchema = z.strictObject({ motd: z.string() });
